import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import BlogRssFeedsPanel from '@/components/admin/BlogRssFeedsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Trash2, Eye, Plus, X, ShieldAlert, Image as ImageIcon, Upload, Send, Sparkles, Share2 } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillTableBetter from 'quill-table-better';
import 'quill-table-better/dist/quill-table-better.css';
import { BlogCoverDropzone } from '@/components/admin/BlogCoverDropzone';
import { RewriteWithAiDialog } from '@/components/admin/RewriteWithAiDialog';
import BlogReshareDialog from '@/components/admin/BlogReshareDialog';
import OgPreviewTester from '@/components/blog/OgPreviewTester';

// Register the table-better module once at module load. Quill 2.x's
// built-in table is intentionally minimal (no row/col ops, no merge,
// no resize); table-better gives the blog editor a full table UI
// (insert table, add/delete row+col, merge, resize, copy/paste, etc.)
// so existing tables in published posts can be edited and new tables
// can be authored cleanly.
(() => {
  if (typeof window === 'undefined') return;
  if (window.__cca_table_better_registered) return;
  try {
    Quill.register({ 'modules/table-better': QuillTableBetter }, true);
    window.__cca_table_better_registered = true;
  } catch (e) {
    // Re-registration is fine — Quill warns but works.
  }
})();

// Register a "divider" blot once at module load so the custom toolbar
// HR button can call insertEmbed('divider', ...). Quill ships nothing
// for <hr/> out of the box.
(() => {
  if (typeof window === 'undefined') return;
  if (window.__cca_divider_blot_registered) return;
  try {
    const BlockEmbed = Quill.import('blots/block/embed');
    class DividerBlot extends BlockEmbed {}
    DividerBlot.blotName = 'divider';
    DividerBlot.tagName = 'hr';
    Quill.register(DividerBlot, true);
    window.__cca_divider_blot_registered = true;
  } catch (e) {
    // Re-registration is fine — Quill warns but works.
  }
})();

const BLOG_CATEGORIES = [
  'Communication Tips',
  'Neurodivergent Life',
  'Phrase Spotlight',
  'App Updates',
  'Community Stories',
  'Resources',
];

// Keep the visible slug input to URL-safe chars while the admin types:
// lowercase, digits, hyphens (spaces become hyphens live).
const sanitizeSlugInput = (v) =>
  (v || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 80);

// Hashtags: no spaces or punctuation allowed — letters + digits only.
const sanitizeTag = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Legacy posts store tables with <thead>/<th> header markup. Quill's
// table model only understands <tbody><tr><td>, so those header cells
// collapsed into a single text block in the editor (impossible to edit
// column-by-column). Normalize before loading: th → td (content bolded),
// thead/tfoot rows folded into tbody. Saved output renders fine on the
// public page and is fully editable via quill-table-better.
const normalizeLegacyTables = (html) => {
  if (!html || (!html.includes('<thead') && !html.includes('<th'))) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('table').forEach((table) => {
      table.querySelectorAll('th').forEach((th) => {
        const td = doc.createElement('td');
        const strong = doc.createElement('strong');
        strong.innerHTML = th.innerHTML;
        td.appendChild(strong);
        Array.from(th.attributes).forEach((a) => td.setAttribute(a.name, a.value));
        th.replaceWith(td);
      });
      let tbody = table.querySelector('tbody');
      if (!tbody) {
        tbody = doc.createElement('tbody');
        table.appendChild(tbody);
      }
      table.querySelectorAll('thead').forEach((sec) => {
        Array.from(sec.querySelectorAll('tr')).reverse().forEach((r) => {
          tbody.insertBefore(r, tbody.firstChild);
        });
        sec.remove();
      });
      table.querySelectorAll('tfoot').forEach((sec) => {
        Array.from(sec.querySelectorAll('tr')).forEach((r) => tbody.appendChild(r));
        sec.remove();
      });
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

/**
 * Live OG card preview. Debounces title changes by 600ms so we only refresh
 * the image when the author pauses typing (avoids hammering the server while
 * they're editing). Shows the exact 1200x630 PNG that scrapers will display.
 */
const OgCardPreview = ({ title }) => {
  const [debouncedTitle, setDebouncedTitle] = useState(title);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      setDebouncedTitle(title);
      setLoading(false);
    }, 600);
    return () => clearTimeout(t);
  }, [title]);

  const safeTitle = (debouncedTitle || 'Untitled post').slice(0, 200);
  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
  const src = `${apiUrl}/api/og/preview.png?title=${encodeURIComponent(safeTitle)}`;

  return (
    <div data-testid="og-card-preview">
      <Label className="font-medium flex items-center gap-1.5">
        <ImageIcon className="h-4 w-4 text-[#00E5CC]" />
        Social card preview
        <span className="text-xs font-normal text-muted-foreground ml-1">
          (what Twitter / LinkedIn / Facebook will show)
        </span>
      </Label>
      <div className="mt-1.5 rounded-lg overflow-hidden border bg-muted/30 relative">
        <div className="aspect-[1200/630] w-full max-w-2xl">
          <img
            key={src}
            src={src}
            alt="Open Graph card preview"
            className={`w-full h-full object-cover transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}
            loading="lazy"
            data-testid="og-card-preview-img"
          />
        </div>
        {loading && (
          <div className="absolute top-2 right-2 text-[11px] bg-background/85 backdrop-blur-sm rounded-full px-2 py-0.5 text-muted-foreground border" data-testid="og-card-preview-loading">
            Updating…
          </div>
        )}
      </div>
    </div>
  );
};

const BlogEditorPage = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const isEditing = !!postId;
  const canEdit = user?.is_admin || user?.is_contributor;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [coverImage, setCoverImage] = useState('');
  // Public slug of the currently-loaded post — used by the OG link
  // preview tester so admins can verify what social bots will fetch
  // for the live URL without leaving the editor.
  const [postSlug, setPostSlug] = useState('');
  // Editable URL slug (create + edit). Auto-follows the title on new
  // posts until the admin touches the field manually.
  const [slug, setSlug] = useState('');
  const slugTouchedRef = React.useRef(false);
  // Holds a user-selected cover image when there's no postId yet (new
  // post). Uploaded automatically the moment the post is first saved.
  const [pendingCoverFile, setPendingCoverFile] = useState(null);
  const [pendingCoverPreview, setPendingCoverPreview] = useState('');

  // Keep a blob: URL alive for the in-editor preview of staged covers.
  useEffect(() => {
    if (!pendingCoverFile) {
      if (pendingCoverPreview) URL.revokeObjectURL(pendingCoverPreview);
      setPendingCoverPreview('');
      return;
    }
    const url = URL.createObjectURL(pendingCoverFile);
    setPendingCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingCoverFile]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [published, setPublished] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');  // local datetime-local string
  const [saving, setSaving] = useState(false);
  const [allPosts, setAllPosts] = useState([]);
  const [activeTab, setActiveTab] = useState(isEditing ? 'editor' : 'list');
  const [sortMode, setSortMode] = useState('date_desc');

  // "Rewrite with AI" workflow — non-destructive. When the admin clicks
  // the sparkles button on a post row, we open a dialog with the original
  // post on top and an AI rewrite (after they prompt for one) below.
  // Original stays untouched in the DB unless the admin then chooses
  // "Edit & Replace" and presses Save in the editor.
  const [rewriteOpenPost, setRewriteOpenPost] = useState(null);
  // Smart re-share dialog — generates per-platform social drafts for a
  // published blog post in one click. Only available for posts that
  // have already been published (so the captions can point at a live
  // URL). See `BlogReshareDialog` for the workflow.
  const [reshareOpenPost, setReshareOpenPost] = useState(null);

  // Dirty-form / autosave REMOVED May 2026 per user feedback — the
  // restore prompt kept popping up + the "Restore" button wiped fields
  // to blank in some cases. Users explicitly asked for "just allow
  // users to save drafts" via the existing Save button (published=false
  // posts already act as drafts in the data model). No browser-side
  // recovery, no beforeunload warning, no "Unsaved changes" banner.
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Blog categories — fetched from /api/blog/categories. Admins can add /
  // edit / delete via the category manager modal. The legacy hard-coded
  // BLOG_CATEGORIES list is rendered as a fallback for posts that
  // reference an "old" category not yet migrated into the DB collection.
  const [categories, setCategories] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCatSlug, setNewCatSlug] = useState('');
  const [newCatLabel, setNewCatLabel] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const refreshCategories = useCallback(async () => {
    const { data } = await apiFetch('/api/blog/categories');
    if (data?.categories) setCategories(data.categories);
  }, []);
  useEffect(() => { refreshCategories(); }, [refreshCategories]);

  const handleCreateCategory = async () => {
    const slug = newCatSlug.trim().toLowerCase().replace(/\s+/g, '-');
    const label = newCatLabel.trim();
    if (!slug || !label) {
      toast.error('Slug and label are required');
      return;
    }
    setCreatingCategory(true);
    const { data, error } = await apiFetch('/api/blog/admin/categories', {
      method: 'POST',
      body: { slug, label },
    });
    setCreatingCategory(false);
    if (error) { toast.error(error); return; }
    toast.success(`Created "${data.label}"`);
    setNewCatSlug('');
    setNewCatLabel('');
    refreshCategories();
  };

  const handleDeleteCategory = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"? Posts that reference it will keep the value until you re-categorise them.`)) return;
    const { data, error } = await apiFetch(`/api/blog/admin/categories/${slug}`, { method: 'DELETE' });
    if (error) { toast.error(error); return; }
    toast.success(`Deleted. ${data.posts_with_category || 0} posts still reference this slug.`);
    refreshCategories();
  };

  const handleRenameCategory = async (slug, currentLabel) => {
    const next = window.prompt(`Rename "${currentLabel}" to:`, currentLabel);
    if (!next || next.trim() === currentLabel) return;
    const { error } = await apiFetch(`/api/blog/admin/categories/${slug}`, {
      method: 'PUT',
      body: { label: next.trim() },
    });
    if (error) { toast.error(error); return; }
    toast.success('Renamed');
    refreshCategories();
  };

  const quillModules = useMemo(() => ({
    // Disable Quill 2.x's built-in (minimal) table module so table-better
    // can take over without conflict.
    table: false,
    'table-better': {
      // Render the table-better helper toolbar (row/col/merge buttons)
      // when the caret is inside a table cell.
      toolbarTable: true,
      language: 'en_US',
      menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
    },
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        // Table button — table-better renders its own picker (rows × cols
        // grid) when this toolbar key is present.
        ['table-better'],
        // Custom button: section divider (horizontal rule). Quill ships
        // a 'divider' format hook for any name we choose; the matching
        // handler below inserts <hr/>. We give the button a CSS class
        // so we can render the "—" glyph in the toolbar.
        [{ 'divider': 'hr' }],
        ['clean']
      ],
      handlers: {
        // Custom HR handler — insert a horizontal rule at the cursor
        // position. Uses Quill's blot embed so the HR survives copy /
        // paste / Save cycles cleanly.
        divider: function () {
          const quill = this.quill;
          const range = quill.getSelection(true);
          if (!range) return;
          // Newline before the HR so it doesn't fuse with the
          // previous paragraph, then the HR (rendered server-side
          // as a normal <hr/> tag), then a fresh line for typing.
          quill.insertText(range.index, '\n', 'user');
          quill.insertEmbed(range.index + 1, 'divider', true, 'user');
          quill.insertText(range.index + 2, '\n', 'user');
          quill.setSelection(range.index + 3, 0, 'silent');
        },
      },
    },
    // Strip inherited inline styles (Google Docs / Notion paste leaves
    // background highlights, weird fonts, and stale colours that the
    // "Tx" eraser can't easily remove). The matcher below scrubs the
    // most common offenders during the paste step so the editor stays
    // clean from the start.
    clipboard: {
      matchVisual: false,
      matchers: [
        ['*', (node, delta) => {
          // Drop background, font-family, and inherited line-height from
          // any pasted node before Quill writes it into the editor.
          (delta.ops || []).forEach((op) => {
            if (op.attributes) {
              if (op.attributes.background) delete op.attributes.background;
              if (op.attributes.font) delete op.attributes.font;
            }
          });
          return delta;
        }],
      ],
    },
    keyboard: {
      // Merge table-better's cell-navigation bindings (Tab to next cell,
      // Shift+Tab to previous, Enter handling inside cells, etc.) with
      // our blockquote-soft-break binding.
      bindings: {
        ...(QuillTableBetter.keyboardBindings || {}),
        // Allow soft line breaks inside blockquotes — pressing Enter
        // inside a <blockquote> now inserts a <br> instead of exiting the
        // quote, so multi-paragraph quotes stay attributed to a single
        // speaker. Shift+Enter still works the same.
        blockquoteSoftBreak: {
          key: 13, // Enter
          format: ['blockquote'],
          handler: function (range) {
            // Insert a <br> at the current cursor position and stay
            // inside the blockquote format.
            this.quill.insertText(range.index, '\n', 'user');
            this.quill.setSelection(range.index + 1, 0, 'silent');
          },
        },
      },
    },
  }), []);

  // Add hover tooltips to the Quill toolbar buttons so it's obvious
  // what each icon does. Quill renders the toolbar as static DOM, so
  // we set `title` attributes once after the editor mounts.
  const quillRef = React.useRef(null);

  // quill-table-better caveat (documented in its README): content that
  // contains tables must be loaded via clipboard.convert + updateContents.
  // ReactQuill's `value` prop path uses setContents, which renders every
  // table as an empty <temporary> placeholder. So the editor below is
  // uncontrolled (defaultValue) and ALL programmatic content loads go
  // through this helper.
  const loadContentIntoEditor = useCallback((html, attempt = 0) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      if (attempt < 20) setTimeout(() => loadContentIntoEditor(html, attempt + 1), 100);
      else setContent(html);
      return;
    }
    editor.setContents([], 'silent');
    if (html) {
      const delta = editor.clipboard.convert({ html });
      // Quill's core clipboard also tags table cells with its built-in
      // `table` attribute, which conflicts with table-better's
      // `table-cell` blots and makes Quill drop every cell line
      // (leaving an empty table). Strip the built-in attr.
      delta.ops.forEach((op) => {
        if (op.attributes && op.attributes.table &&
            (op.attributes['table-cell'] || op.attributes['table-cell-block'] ||
             op.attributes['table-th'] || op.attributes['table-th-block'])) {
          delete op.attributes.table;
        }
      });
      // USER source is required: table-better finalises pasted tables
      // during user-sourced updates (README-documented init sequence).
      editor.updateContents(delta, 'user');
    }
    editor.history?.clear?.();
    setContent(editor.root.innerHTML === '<p><br></p>' ? '' : editor.root.innerHTML);
  }, []);

  useEffect(() => {
    // Wait a tick for Quill to render its toolbar
    const t = setTimeout(() => {
      const tooltips = {
        '.ql-bold':              'Bold (⌘B)',
        '.ql-italic':            'Italic (⌘I)',
        '.ql-underline':         'Underline (⌘U)',
        '.ql-strike':            'Strikethrough',
        '.ql-blockquote':        'Block quote — press Enter inside for a line break in the same quote',
        '.ql-code-block':        'Code block',
        '.ql-link':              'Insert link',
        '.ql-image':             'Insert image',
        '.ql-divider':           'Insert section divider (horizontal line)',
        '.ql-clean':             'Clear formatting (highlights, fonts, colours)',
        '.ql-header':            'Heading style',
        '.ql-font':              'Font family',
        '.ql-size':              'Text size',
        '.ql-color':             'Text colour',
        '.ql-background':        'Highlight colour',
        '.ql-align':             'Text alignment',
        '.ql-list[value="ordered"]': 'Numbered list',
        '.ql-list[value="bullet"]':  'Bullet list',
        '.ql-table-better':      'Insert table (then click inside any cell for row/column controls)',
      };
      Object.entries(tooltips).forEach(([sel, title]) => {
        document.querySelectorAll(`.ql-toolbar ${sel}`).forEach((el) => {
          el.setAttribute('title', title);
          el.setAttribute('aria-label', title);
        });
      });
    }, 200);
    return () => clearTimeout(t);
  }, [postId]);

  // Load post for editing
  useEffect(() => {
    if (postId) {
      // When the AI rewrite dialog handed us a staged draft in
      // sessionStorage, we MUST skip the post fetch — otherwise the
      // fetch resolves after the AI hydration effect and overwrites
      // the new content with the original. We still need the
      // existing-post metadata (category, tags, published flag,
      // scheduled_at) the AI rewrite doesn't carry, so we read those
      // selectively, but the title/content/excerpt are owned by the
      // AI hydration step in this mode.
      const params = new URLSearchParams(window.location.search);
      const isAiRewrite = params.get('from') === 'ai-rewrite' &&
                          typeof window !== 'undefined' &&
                          sessionStorage.getItem('aiBlogDraft');
      (async () => {
        const { data } = await apiFetch(`/api/blog/posts/${postId}`);
        if (data) {
          if (!isAiRewrite) {
            setTitle(data.title || '');
            loadContentIntoEditor(normalizeLegacyTables(data.content || ''));
            setExcerpt(data.excerpt || '');
            setSelectedCats(
              Array.isArray(data.categories) && data.categories.length
                ? data.categories
                : (data.category ? [data.category] : [])
            );
            setCoverImage(data.cover_image || '');
            setTags(data.tags || []);
          } else {
            // AI-rewrite mode: only seed fields the AI doesn't own.
            // The cover image carries over so the rewrite reuses the
            // existing hero unless the admin replaces it manually.
            setCoverImage(data.cover_image || '');
          }
          setPostSlug(data.slug || '');
          setSlug(data.slug || '');
          slugTouchedRef.current = true;
          setPublished(data.published || false);
          // Convert ISO -> datetime-local "YYYY-MM-DDTHH:MM" for the input
          if (data.scheduled_at) {
            try {
              const dt = new Date(data.scheduled_at);
              const pad = (n) => String(n).padStart(2, '0');
              const local = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
              setScheduledAt(local);
            } catch (e) { /* ignore */ }
          }
          setActiveTab('editor');
          // Mark load done so the editor accepts further edits. No
          // autosave snapshot to check (removed May 2026).
          setTimeout(() => { setInitialLoadDone(true); }, 50);
        }
      })();
    } else {
      setTimeout(() => setInitialLoadDone(true), 50);
    }
  }, [postId]);

  // Load all posts for admin list
  const fetchPosts = useCallback(async () => {
    const { data } = await apiFetch('/api/blog/admin/posts');
    if (data?.posts) setAllPosts(data.posts);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // New posts: the slug field mirrors the title (readable, SEO-friendly)
  // until the admin edits the slug directly.
  useEffect(() => {
    if (isEditing || slugTouchedRef.current) return;
    setSlug(sanitizeSlugInput(title).replace(/^-+|-+$/g, ''));
  }, [title, isEditing]);

  // Pick up an AI-generated draft stashed in sessionStorage by either:
  //   • the AI Blog Generator admin tab (?from=ai-draft, NEW post)
  //   • the "Rewrite with AI" dialog (?from=ai-rewrite, EXISTING post)
  // Hydrate the editor form once, then drop the storage so it isn't
  // reapplied on refresh.
  //
  // The backend now does Markdown → HTML conversion server-side and
  // returns it as `content_html`, so the editor gets clean, Quill-
  // ready HTML with no client-side parsing needed. `marked` is kept
  // as a fallback for older drafts that pre-date the backend conversion.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    const isAiDraft = from === 'ai-draft' || from === 'ai-rewrite';
    if (!isAiDraft) return;
    // For ai-draft (new post) we wait until !isEditing. For ai-rewrite
    // (replacing existing post) we wait until the post load has finished
    // so we don't race the load-into-state effect.
    if (from === 'ai-draft' && isEditing) return;
    if (from === 'ai-rewrite' && !initialLoadDone) return;
    (async () => {
      try {
        const raw = sessionStorage.getItem('aiBlogDraft');
        if (!raw) return;
        const d = JSON.parse(raw);
        let contentHtml = '';
        if (d.content_html && d.content_html.trim()) {
          contentHtml = d.content_html;
        } else if (d.content) {
          const { marked } = await import('marked');
          marked.use({
            gfm: true,
            breaks: false,
            mangle: false,
            headerIds: false,
          });
          contentHtml = marked.parse(String(d.content));
        }
        let excerptText = d.excerpt ? String(d.excerpt) : '';
        if (excerptText && /[*_`\[]/.test(excerptText)) {
          try {
            const { marked } = await import('marked');
            const tmp = document.createElement('div');
            tmp.innerHTML = marked.parseInline(excerptText);
            excerptText = (tmp.textContent || excerptText).trim();
          } catch { /* ignore */ }
        }
        setTitle(d.title || '');
        loadContentIntoEditor(normalizeLegacyTables(contentHtml));
        setExcerpt(excerptText);
        if (d.category) setSelectedCats([d.category]);
        if (Array.isArray(d.tags)) setTags(d.tags.slice(0, 8));
        if (d.cover_image) setCoverImage(d.cover_image);
        setActiveTab('editor');
        sessionStorage.removeItem('aiBlogDraft');
        if (from === 'ai-rewrite') {
          toast.info('AI rewrite loaded. Save to replace the original — or hit Back to keep the original.');
        }
      } catch (e) {
        console.warn('Failed to load AI draft', e);
      }
    })();
  }, [isEditing, initialLoadDone]);

  // Backspace shortcut — when actively editing a post, pressing
  // Backspace anywhere OUTSIDE an input / textarea / contentEditable
  // navigates back to the Blog Manager list. Mirrors classic CMS
  // editors so power-users can bail out without reaching for the
  // mouse. Skipped while typing so we don't eat character deletes.
  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e) => {
      if (e.key !== 'Backspace') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      const editable = e.target?.isContentEditable;
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || editable) return;
      e.preventDefault();
      if (window.history.length > 1 && document.referrer && document.referrer.includes(window.location.host)) {
        navigate(-1);
      } else {
        navigate('/blog/admin');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEditing, navigate]);

  const addTag = () => {
    const t = sanitizeTag(newTag);
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setNewTag('');
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const handleSave = async (publish = null) => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!content.trim()) { toast.error('Content is required'); return; }
    setSaving(true);

    const body = {
      title: title.trim(),
      content,
      excerpt: excerpt.trim() || undefined,
      category: selectedCats[0] || undefined,
      categories: selectedCats,
      cover_image: coverImage.trim() || undefined,
      slug: slug.replace(/^-+|-+$/g, '') || undefined,
      tags,
      published: publish !== null ? publish : published
    };

    // Convert local datetime-local string to ISO if scheduling is set
    if (scheduledAt) {
      try {
        body.scheduled_at = new Date(scheduledAt).toISOString();
      } catch (e) {
        // Invalid date — drop the field
      }
    } else {
      body.scheduled_at = null;
    }

    try {
      if (isEditing) {
        const { error } = await apiFetch(`/api/blog/admin/posts/${postId}`, { method: 'PUT', body });
        if (error) { toast.error(error); return; }
        toast.success('Post updated!');
        // Return to the blog manager so the admin sees the saved post
        // in the list. Avoids the "save and stay in the editor with no
        // visible confirmation" UX trap.
        fetchPosts();
        navigate('/blog/admin');
        return;
      } else {
        const { data, error } = await apiFetch('/api/blog/admin/posts', { method: 'POST', body });
        if (error) { toast.error(error); return; }
        // If the user staged a cover before the first save, upload it
        // now that the post exists. The dropzone normally handles this
        // directly when postId is present; for "new" posts we orchestrate
        // it here so the cover lands in MongoDB before navigating away.
        if (pendingCoverFile && data?.post_id) {
          try {
            const form = new FormData();
            form.append('file', pendingCoverFile);
            const { getAuthHeaders } = await import('@/utils/authUtils');
            const res = await fetch(`/api/blog/admin/posts/${data.post_id}/cover-image`, {
              method: 'POST',
              body: form,
              credentials: 'include',
              headers: getAuthHeaders(),
            });
            if (res.ok) {
              const cd = await res.json();
              setCoverImage(cd.cover_image || '');
              setPendingCoverFile(null);
            } else {
              toast.warning('Post saved, but the cover upload failed — try again in the editor.');
            }
          } catch (e) {
            toast.warning(`Post saved, but cover upload errored: ${e.message}`);
          }
        }
        toast.success(body.published ? 'Post published!' : 'Draft saved!');
        // Send the admin to the blog manager rather than the edit page —
        // matches user expectation after clicking "Save" (they want to
        // see their post in the list, not stay in editor limbo).
        fetchPosts();
        navigate('/blog/admin');
      }
      fetchPosts();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    const { error } = await apiFetch(`/api/blog/admin/posts/${id}`, { method: 'DELETE' });
    if (error) { toast.error(error); return; }
    toast.success('Post deleted');
    if (postId === id) navigate('/blog/admin');
    fetchPosts();
  };

  const resetForm = () => {
    setTitle(''); loadContentIntoEditor(''); setExcerpt(''); setSelectedCats([]);
    setCoverImage(''); setTags([]); setPublished(false); setScheduledAt('');
    setSlug(''); setPostSlug(''); slugTouchedRef.current = false;
    // NOTE: do NOT call navigate('/blog/admin') here — when this fires
    // from the "New Post" button (already on /blog/admin), React Router
    // can re-run the parent route's useEffect and snap activeTab back
    // to 'list', leaving the user staring at a blank editor pane.
    // Callers that need URL changes (e.g. after deleting the current
    // post being edited) should navigate explicitly.
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <Header />
      {authLoading ? (
        <div className="container py-20 text-center text-muted-foreground" data-testid="blog-editor-auth-loading">
          Loading editor…
        </div>
      ) : !isAuthenticated || !canEdit ? (
        <div className="container py-20 text-center">
          <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-xl font-medium mb-2">Access Restricted</p>
          <p className="text-muted-foreground mb-4">Only admins and blog contributors can access this page.</p>
          <Button onClick={() => navigate('/blog')} className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]">
            Back to Blog
          </Button>
        </div>
      ) : (
      <div className="container py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {/* When the admin is actively editing a post, surface a
                  prominent "back to manager" affordance. We use both
                  browser-history back AND a hard fallback to the /blog/
                  admin route so it works whether the editor was opened
                  via list-click or via deep link / refresh. */}
              {isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Prefer history-back when there's a previous
                    // page on the same origin so the admin keeps any
                    // list scroll/filter state. Otherwise force-route
                    // to the manager.
                    if (window.history.length > 1 && document.referrer && document.referrer.includes(window.location.host)) {
                      navigate(-1);
                    } else {
                      navigate('/blog/admin');
                    }
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="back-to-blog-manager-btn"
                  title="Back to Blog Manager (or press Backspace)"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              <h1 className="text-2xl font-bold">Blog Manager</h1>
            </div>
            <div className="flex gap-2">
              <Button variant={activeTab === 'list' ? 'default' : 'outline'} size="sm" onClick={() => { setActiveTab('list'); resetForm(); }}>
                All Posts
              </Button>
              <Button variant={activeTab === 'editor' ? 'default' : 'outline'} size="sm" onClick={() => { setActiveTab('editor'); resetForm(); }}>
                <Plus className="h-4 w-4 mr-1" /> New Post
              </Button>
            </div>
          </div>

          {activeTab === 'list' && (
            <div className="space-y-3">
              <BlogRssFeedsPanel />
              {/* Sort controls — A-Z, Z-A, Category, Newest/Oldest first */}
              {allPosts.length > 0 && (
                <div
                  className="flex items-center justify-between gap-2 pb-1"
                  data-testid="blog-sort-row"
                >
                  <p className="text-xs text-muted-foreground">
                    {allPosts.length} post{allPosts.length === 1 ? '' : 's'}
                  </p>
                  <Select value={sortMode} onValueChange={setSortMode}>
                    <SelectTrigger
                      className="w-auto min-w-[180px] h-8 text-xs"
                      data-testid="blog-sort-select"
                    >
                      <SelectValue placeholder="Sort by…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date_desc">Date posted (newest first)</SelectItem>
                      <SelectItem value="date_asc">Date posted (oldest first)</SelectItem>
                      <SelectItem value="title_asc">Title A → Z</SelectItem>
                      <SelectItem value="title_desc">Title Z → A</SelectItem>
                      <SelectItem value="category">Category (A → Z)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {allPosts.length === 0 ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground">No posts yet. Create your first one!</CardContent></Card>
              ) : [...allPosts].sort((a, b) => {
                const aT = (a.title || '').toLowerCase();
                const bT = (b.title || '').toLowerCase();
                const aD = a.published_at || a.created_at || '';
                const bD = b.published_at || b.created_at || '';
                const aC = (a.category || '').toLowerCase();
                const bC = (b.category || '').toLowerCase();
                if (sortMode === 'title_asc') return aT.localeCompare(bT);
                if (sortMode === 'title_desc') return bT.localeCompare(aT);
                if (sortMode === 'date_asc') return aD.localeCompare(bD);
                if (sortMode === 'category') return aC.localeCompare(bC) || aT.localeCompare(bT);
                return bD.localeCompare(aD); // date_desc default
              }).map(post => (
                <Card key={post.post_id} className="hover:shadow-md transition-shadow" data-testid={`admin-post-${post.post_id}`}>
                  <CardContent className="p-3 sm:py-4 sm:px-6 flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Top row on mobile: thumbnail + title block side by side */}
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="shrink-0 w-16 h-12 rounded overflow-hidden bg-muted border border-border flex items-center justify-center">
                        {post.cover_image ? (
                          <img
                            src={post.cover_image}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.add('bg-amber-100'); e.currentTarget.parentElement.title = 'Cover image is broken'; }}
                          />
                        ) : (
                          <span className="text-[10px] text-muted-foreground text-center px-1">no cover</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-medium text-sm sm:text-base line-clamp-2 sm:truncate flex-1 min-w-0">{post.title}</h3>
                          <Badge variant={post.published ? 'default' : 'secondary'} className="text-[10px] sm:text-xs shrink-0">
                            {post.published ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                        <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-2">
                          {post.category && <span>{post.category} · </span>}
                          {post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Not published'} · {post.views || 0} views
                        </p>
                      </div>
                    </div>
                    {/* Action buttons — full row on mobile, inline on desktop */}
                    <div className="flex gap-1 justify-end sm:ml-3 sm:shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/blog/${post.post_id}`)} title="Preview">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRewriteOpenPost(post)}
                        title="Rewrite with AI — your original stays safe until you save"
                        className="text-[#00A896] hover:text-[#008b7a] hover:bg-[#00E5CC]/10"
                        data-testid={`rewrite-with-ai-btn-${post.post_id}`}
                      >
                        <Sparkles className="h-4 w-4" />
                      </Button>
                      {post.published && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReshareOpenPost(post)}
                          title="Smart re-share — auto-create FB / IG / Threads / LinkedIn / X drafts"
                          className="text-[#00A896] hover:text-[#008b7a] hover:bg-[#00E5CC]/10"
                          data-testid={`smart-reshare-btn-${post.post_id}`}
                        >
                          <Share2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/blog/admin/edit/${post.post_id}`)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(post.post_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'editor' && (
            <div className="space-y-4">
              {/* Title */}
              <div>
                <Label className="font-medium">Title</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Post title..." className="mt-1 text-lg" data-testid="blog-title-input" />
              </div>

              {/* URL slug — editable on create AND edit. New posts mirror
                  the title until the admin types here. Changing an existing
                  post's slug keeps the old URL working via a redirect. */}
              <div>
                <Label className="font-medium">URL slug</Label>
                <div className="flex items-stretch mt-1">
                  <span className="inline-flex items-center text-xs text-muted-foreground bg-muted border border-r-0 border-input rounded-l-md px-2 whitespace-nowrap select-none">
                    clearcommunicationapp.com/blog/
                  </span>
                  <Input
                    value={slug}
                    onChange={e => { slugTouchedRef.current = true; setSlug(sanitizeSlugInput(e.target.value)); }}
                    placeholder="my-post-url"
                    className="rounded-l-none font-mono text-sm"
                    data-testid="blog-slug-input"
                  />
                </div>
                {isEditing && postSlug && slug.replace(/^-+|-+$/g, '') !== postSlug ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1" data-testid="blog-slug-change-note">
                    URL will change on save — the old link (/blog/{postSlug}) will automatically redirect here.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    Lowercase letters, numbers, and hyphens only. Keep it short and readable for best Google indexing.
                  </p>
                )}
              </div>

              {/* Live OG card preview — exact image Twitter / LinkedIn / Facebook will show */}
              <OgCardPreview title={title} />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Categories</Label>
                    <button
                      type="button"
                      onClick={() => setShowCategoryManager(true)}
                      className="text-xs text-[#00A896] hover:underline"
                      data-testid="open-category-manager-btn"
                    >
                      Manage categories
                    </button>
                  </div>
                  {/* Multi-select chips — pick any number; the FIRST one
                      (★) is the primary category shown on cards. All of
                      them count for the per-category RSS feeds. */}
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="blog-category-chips">
                    {[
                      ...categories.map(c => ({ value: c.slug, label: c.label })),
                      ...BLOG_CATEGORIES
                        .filter(c => !categories.some(dc => dc.slug === c || dc.label === c))
                        .map(c => ({ value: c, label: c })),
                    ].map(opt => {
                      const idx = selectedCats.indexOf(opt.value);
                      const isSel = idx >= 0;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setSelectedCats(prev => prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value])}
                          className={`text-xs px-2.5 py-1 rounded-full border transition ${isSel ? 'bg-[#00A896] text-white border-[#00A896]' : 'bg-transparent border-input text-muted-foreground hover:border-[#00A896]/60'}`}
                          data-testid={`category-chip-${opt.value}`}
                        >
                          {opt.label}{idx === 0 && <span className="ml-1 opacity-90">★</span>}
                        </button>
                      );
                    })}
                    {selectedCats
                      .filter(v => !categories.some(c => c.slug === v) && !BLOG_CATEGORIES.includes(v))
                      .map(v => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setSelectedCats(prev => prev.filter(x => x !== v))}
                          className="text-xs px-2.5 py-1 rounded-full border bg-[#00A896] text-white border-[#00A896]"
                          data-testid={`category-chip-${v}`}
                        >
                          {v}{selectedCats.indexOf(v) === 0 && <span className="ml-1 opacity-90">★</span>}
                        </button>
                      ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pick one or more — the first (★) is the primary shown on cards; every selected category includes this post in its RSS feed.
                  </p>
                </div>
                {/* Cover Image — drag-and-drop upload + URL fallback.
                    Used everywhere we serve a social share image: the
                    blog page's og:image, the RSS <enclosure> Metricool
                    pulls, and the /api/og/blog/<slug>.png OG redirect. */}
                <div>
                  <Label className="font-medium">Cover Image</Label>
                  <BlogCoverDropzone
                    postId={postId}
                    coverImage={coverImage}
                    stagedPreviewUrl={pendingCoverPreview}
                    onUploaded={(path) => setCoverImage(path)}
                    onCleared={() => setCoverImage('')}
                    onStageFile={(file) => setPendingCoverFile(file)}
                  />
                  <Input
                    value={coverImage}
                    onChange={e => setCoverImage(e.target.value)}
                    placeholder="…or paste a URL (https://...) or a site path (/blog-covers/foo.jpg)"
                    className="mt-2"
                    data-testid="blog-cover-url-input"
                  />
                  {postSlug && (
                    <div className="mt-3">
                      <OgPreviewTester
                        slug={postSlug}
                        hasCoverImage={!!coverImage}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <Label className="font-medium">Excerpt (optional)</Label>
                <Input value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Short summary for previews..." className="mt-1" />
              </div>

              {/* Tags */}
              <div>
                <Label className="font-medium">Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={newTag} onChange={e => setNewTag(sanitizeTag(e.target.value))} placeholder="Add tag (letters + numbers only)..." className="flex-1"
                    data-testid="blog-tag-input"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
                  <Button type="button" variant="outline" onClick={addTag} size="sm">Add</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Hashtags can&apos;t contain spaces or punctuation — they&apos;re stripped automatically.</p>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map(t => (
                      <Badge key={t} variant="secondary" className="gap-1">
                        #{t}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(t)} />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Rich Text Editor */}
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <Label className="font-medium">Content</Label>
                  <span className="text-xs text-muted-foreground">
                    Tip: highlight pasted text and click the <strong>Tx</strong> eraser to remove highlights / formatting.
                  </span>
                </div>
                <div className="mt-1 bg-white dark:bg-gray-900 rounded-md border [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-border [&_.ql-container]:border-none [&_.ql-editor]:min-h-[300px] [&_.ql-editor]:text-foreground [&_.ql-toolbar_.ql-stroke]:stroke-foreground [&_.ql-toolbar_.ql-fill]:fill-foreground [&_.ql-toolbar_.ql-picker-label]:text-foreground [&_.ql-toolbar_.ql-picker-options]:bg-background [&_.ql-toolbar_.ql-picker-options]:border-border [&_.ql-editor.ql-blank::before]:text-muted-foreground">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    defaultValue=""
                    onChange={setContent}
                    modules={quillModules}
                    placeholder="Write your blog post..."
                  />
                </div>
              </div>

              {/* Schedule for future publish */}
              <div className="border rounded p-3 bg-slate-50 dark:bg-slate-900/60 dark:border-slate-700">
                <Label htmlFor="schedule-at" className="text-sm font-medium flex items-center gap-2 text-foreground">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                  Schedule publish (optional)
                </Label>
                <div className="flex gap-2 mt-1 items-center">
                  <Input
                    id="schedule-at"
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="max-w-xs"
                    data-testid="schedule-at-input"
                  />
                  {scheduledAt && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setScheduledAt('')}
                      data-testid="schedule-clear"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  When set, the post auto-publishes at this local time (checked every 5 min). Leave blank to publish manually.
                </p>
              </div>

              {/* Publish toggle & actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-3">
                  <Switch checked={published} onCheckedChange={setPublished} data-testid="publish-toggle" />
                  <Label>{published ? 'Published' : (scheduledAt ? 'Scheduled' : 'Draft')}</Label>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {isEditing && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        const { data, error } = await apiFetch(
                          `/api/blog/admin/posts/${postId}/push-to-notion`,
                          { method: 'POST', body: {} },
                        );
                        if (error) return toast.error(error);
                        if (data?.notion_url) {
                          toast.success('Pushed to Notion ✓', {
                            duration: 8000,
                            action: { label: 'Open in Notion', onClick: () => window.open(data.notion_url, '_blank') },
                          });
                        } else {
                          toast.info('Notion not configured — set it up in Admin → Notion.');
                        }
                      }}
                      data-testid="push-to-notion-btn"
                      className="border-[#A78BFA]/40 hover:bg-[#A78BFA]/10 text-purple-700 dark:text-purple-300"
                      title="Push this post to your Notion DB (auto-fires on save too)"
                    >
                      <Send className="h-4 w-4 mr-1" /> Push to Notion
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { resetForm(); setActiveTab('list'); if (isEditing) navigate('/blog/admin'); }}>Cancel</Button>
                  <Button onClick={() => handleSave(false)} disabled={saving} variant="secondary">
                    <Save className="h-4 w-4 mr-1" /> Save Draft
                  </Button>
                  <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]">
                    <Save className="h-4 w-4 mr-1" /> {isEditing ? 'Update & Publish' : 'Publish'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Category Manager modal — visible to admins only.
          Lets admins create, rename, and delete blog categories without
          leaving the editor. */}
      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent className="sm:max-w-lg" data-testid="category-manager-dialog">
          <DialogHeader>
            <DialogTitle>Manage Blog Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Existing categories</p>
              {categories.length === 0 ? (
                <p className="text-sm text-muted-foreground">No custom categories yet. The legacy fallback list is still available in the dropdown.</p>
              ) : (
                <ul className="space-y-1.5">
                  {categories.map((c) => (
                    <li key={c.slug} className="flex items-center justify-between text-sm">
                      <span><span className="font-medium">{c.label}</span> <span className="text-muted-foreground">({c.slug})</span></span>
                      <span className="flex gap-2">
                        <button type="button" onClick={() => handleRenameCategory(c.slug, c.label)} className="text-xs text-[#00A896] hover:underline" data-testid={`rename-cat-${c.slug}`}>Rename</button>
                        {user?.is_admin && (
                          <button type="button" onClick={() => handleDeleteCategory(c.slug)} className="text-xs text-red-600 hover:underline" data-testid={`delete-cat-${c.slug}`}>Delete</button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {user?.is_admin && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Add new category</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input
                    value={newCatSlug}
                    onChange={(e) => setNewCatSlug(e.target.value)}
                    placeholder="slug (e.g. parenting)"
                    data-testid="new-cat-slug-input"
                  />
                  <Input
                    value={newCatLabel}
                    onChange={(e) => setNewCatLabel(e.target.value)}
                    placeholder="Display label (e.g. Parenting)"
                    data-testid="new-cat-label-input"
                  />
                </div>
                <Button
                  onClick={handleCreateCategory}
                  disabled={creatingCategory || !newCatSlug.trim() || !newCatLabel.trim()}
                  className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] w-full"
                  data-testid="create-cat-btn"
                >
                  {creatingCategory ? 'Adding…' : 'Add category'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* "Rewrite with AI" dialog — opens from the sparkles button on
          each post row. Non-destructive: original stays safe in the DB
          until the admin presses Save in the editor. */}
      <RewriteWithAiDialog
        post={rewriteOpenPost}
        open={!!rewriteOpenPost}
        onOpenChange={(o) => { if (!o) setRewriteOpenPost(null); }}
        onAcceptForEdit={(postId) => {
          // Hand off via sessionStorage (set inside the dialog) — go straight
          // to the editor for that post. The hydration effect picks up the
          // aiBlogDraft and seeds title/content/excerpt/etc.
          navigate(`/blog/admin/edit/${postId}?from=ai-rewrite`);
        }}
      />

      {/* Smart re-share dialog — auto-generates per-platform social drafts
          (FB / IG / Threads / LinkedIn / X) from a published blog post.
          Only shown for `post.published === true`. */}
      <BlogReshareDialog
        post={reshareOpenPost}
        open={!!reshareOpenPost}
        onOpenChange={(o) => { if (!o) setReshareOpenPost(null); }}
      />
    </div>
  );
};

export default BlogEditorPage;
